import React from 'react';
import { 
  Search, 
  X, 
  ChevronRight, 
  Plus, 
  Bot, 
  Clock, 
  Award,
  Play,
  RefreshCw,
  Eye,
  CheckSquare,
  FileVideo
} from 'lucide-react';
import type { Perfil, Topico, Subtopico } from '../types';
import { getProfileMetrics } from './EcadCatalogDashboard';
import { EcadProcessSidebar } from './EcadProcessSidebar';

interface EcadTopicSummaryProps {
  selectedPerfil: Perfil | undefined;
  selectedPerfilId: string | null;
  selectedTopicoId: string | null;
  setSelectedPerfilId: (id: string | null) => void;
  setSelectedTopicoId: (id: string | null) => void;
  setSelectedSubtopicoId: (id: string | null) => void;
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  setSubtopicoForm: (form: any) => void;
  setShowSubtopicoModal: (val: boolean) => void;
  onTriggerAction?: (action: string, detail?: string) => void;

  globalProcStatus?: 'idle' | 'running' | 'stopping' | 'stopped';
  setGlobalProcStatus?: React.Dispatch<React.SetStateAction<'idle' | 'running' | 'stopping' | 'stopped'>>;
  globalProcProgress?: number;
  setGlobalProcProgress?: React.Dispatch<React.SetStateAction<number>>;
  globalProcTimer?: number;
  setGlobalProcTimer?: React.Dispatch<React.SetStateAction<number>>;
  globalProcLogs?: string[];
  setGlobalProcLogs?: React.Dispatch<React.SetStateAction<string[]>>;
  globalAutoShutdown?: boolean;
  setGlobalAutoShutdown?: React.Dispatch<React.SetStateAction<boolean>>;
  completedRoteiroIds?: string[];
  queuedRoteiros?: Subtopico[];
  activeRoteiro?: Subtopico | null;
}

export function EcadTopicSummary({
  selectedPerfil,
  selectedPerfilId,
  selectedTopicoId,
  setSelectedPerfilId,
  setSelectedTopicoId,
  setSelectedSubtopicoId,
  searchQuery,
  setSearchQuery,
  setSubtopicoForm,
  setShowSubtopicoModal,
  onTriggerAction,
  globalProcStatus = 'idle',
  setGlobalProcStatus = () => {},
  globalProcProgress = 0,
  setGlobalProcProgress = () => {},
  globalProcTimer = 0,
  setGlobalProcTimer = () => {},
  globalProcLogs = [],
  setGlobalProcLogs = () => {},
  globalAutoShutdown = true,
  setGlobalAutoShutdown = () => {},
  completedRoteiroIds = [],
  queuedRoteiros = [],
  activeRoteiro = null,
}: EcadTopicSummaryProps) {
  const selectedTopico = selectedPerfil?.topicos.find((t) => t.id === selectedTopicoId);

  const metrics = React.useMemo(() => {
    if (!selectedPerfil) return { pendentes: 0, feedbacks: 0, sugestoes: 0 };
    return getProfileMetrics(selectedPerfil.nome);
  }, [selectedPerfil]);

  const handleStartProcess = () => {
    if (selectedTopico && onTriggerAction) {
      onTriggerAction('processar-topico', selectedTopico.titulo);
    }
  };

  const handleStopContainers = () => {
    if (onTriggerAction) {
      onTriggerAction('acompanha');
    }
    if (setGlobalProcStatus) {
      setGlobalProcStatus('stopping');
      setTimeout(() => {
        setGlobalProcStatus('stopped');
      }, 2000);
    }
  };

  const handleReset = () => {
    if (setGlobalProcStatus) setGlobalProcStatus('idle');
    if (setGlobalProcProgress) setGlobalProcProgress(0);
    if (setGlobalProcTimer) setGlobalProcTimer(0);
    if (setGlobalProcLogs) setGlobalProcLogs([]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', animation: 'fadeIn 0.25s ease-out' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: '16px' }}>
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setSelectedPerfilId(null);
            setSelectedTopicoId(null);
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#1e293b')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
        >
          Catálogo
        </span>
        <ChevronRight size={10} />
        <span
          style={{ cursor: 'pointer', color: selectedPerfil?.isInterno ? '#7c3aed' : '#2563eb' }}
          onClick={() => setSelectedTopicoId(null)}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          {selectedPerfil?.nome}
        </span>
        <ChevronRight size={10} />
        <span style={{ color: '#1e293b' }}>{selectedTopico?.titulo}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.5px' }}>
            Tópico: <span style={{ color: '#7c3aed' }}>{selectedTopico?.titulo}</span>
          </h1>
          <p style={{ fontSize: '0.88rem', color: '#64748b', margin: 0 }}>
            Visualize os roteiros de treinamento e simulações ativas neste tópico de aprendizagem.
          </p>
        </div>

        {/* Right side container with search and new action */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Sistema de Busca Eficiente no Tópico */}
          <div style={{ position: 'relative', width: '240px' }}>
            <input
              id="ecad-topico-search"
              name="ecad-topico-search"
              autoComplete="off"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pesquisar..."
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                color: '#1e293b',
                fontSize: '0.78rem',
                outline: 'none',
                transition: 'all 0.2s',
                fontWeight: 500,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#7c3aed';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(124, 58, 237, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.02)';
              }}
            />
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <button
            onClick={() => {
              setSubtopicoForm({
                id: '',
                topicoId: selectedTopicoId || '',
                titulo: '',
                descricao: '',
                duracao: '2m 30s',
                dificuldade: 'Básico',
                videoUrl: '',
                pdfUrl: '',
              });
              setShowSubtopicoModal(true);
            }}
            style={{
              background: 'rgba(124, 58, 237, 0.08)',
              border: '1px solid rgba(124, 58, 237, 0.2)',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#7c3aed',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(124, 58, 237, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(124, 58, 237, 0.08)';
            }}
          >
            <Plus size={14} /> Novo Roteiro
          </button>
        </div>
      </div>

      {/* Grid Layout: Main Content (Left) + Process Monitor Sidebar (Right) */}
      <div style={{ display: 'flex', gap: '20px', width: '100%', alignItems: 'flex-start' }}>
        
        {/* Coluna da Esquerda: Busca + Roteiros */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {!searchQuery ? (
            <>
              <div style={{ width: '100%', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                    Roteiros & Capacitações
                  </h3>
                </div>

                {selectedTopico?.subtopicos.length === 0 ? (
                  <div style={{ background: '#ffffff', border: '1px dashed #e2e8f0', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
                    Nenhum roteiro cadastrado neste tópico.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                    {selectedTopico?.subtopicos.map((sub, idx) => {
                      const isRoteiroActive = activeRoteiro !== null && activeRoteiro.id === sub.id;
                      const isRoteiroCompleted = completedRoteiroIds.includes(sub.id);
                      const isRoteiroQueued = queuedRoteiros.some(qr => qr.id === sub.id);
                      const queuePosition = isRoteiroQueued ? queuedRoteiros.findIndex(qr => qr.id === sub.id) + 1 : -1;

                      return (
                        <div
                          key={sub.id}
                          onClick={() => setSelectedSubtopicoId(sub.id)}
                          style={{
                            background: '#ffffff',
                            border: isRoteiroActive 
                              ? '1px solid #7c3aed' 
                              : isRoteiroCompleted 
                                ? '1px solid #10b981' 
                                : '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '20px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            boxShadow: isRoteiroActive 
                              ? '0 0 14px rgba(124, 58, 237, 0.25)' 
                              : '0 1px 3px rgba(0,0,0,0.02)',
                            animation: isRoteiroActive ? 'pulseBorder-anim 1.5s infinite alternate' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = isRoteiroActive ? '#7c3aed' : '#7c3aed';
                            e.currentTarget.style.boxShadow = isRoteiroActive 
                              ? '0 0 18px rgba(124, 58, 237, 0.35)' 
                              : '0 4px 12px rgba(124, 58, 237, 0.05)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = isRoteiroActive 
                              ? '#7c3aed' 
                              : isRoteiroCompleted 
                                ? '#10b981' 
                                : '#e2e8f0';
                            e.currentTarget.style.boxShadow = isRoteiroActive 
                              ? '0 0 14px rgba(124, 58, 237, 0.25)' 
                              : '0 1px 3px rgba(0,0,0,0.02)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <style dangerouslySetInnerHTML={{ __html: `
                            @keyframes pulseBorder-anim {
                              from { border-color: #7c3aed; box-shadow: 0 0 4px rgba(124, 58, 237, 0.2); }
                              to { border-color: #a78bfa; box-shadow: 0 0 14px rgba(124, 58, 237, 0.45); }
                            }
                            @keyframes pulseText-anim {
                              from { opacity: 0.6; }
                              to { opacity: 1; }
                            }
                            .pulse-active-badge {
                              animation: pulseText-anim 1s infinite alternate;
                            }
                          `}} />

                          {sub.videoUrl && (
                            <div 
                              onClick={(e) => e.stopPropagation()} 
                              onMouseDown={(e) => e.stopPropagation()}
                              style={{ 
                                width: '100%', 
                                borderRadius: '8px', 
                                overflow: 'hidden', 
                                border: '1px solid #e2e8f0', 
                                background: '#000000',
                                aspectRatio: '16/9',
                                marginBottom: '4px'
                              }}
                            >
                              <video 
                                src={sub.videoUrl} 
                                controls 
                                style={{ width: '100%', height: '100%', display: 'block' }} 
                              />
                            </div>
                          )}
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                              <h4 style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', margin: 0, lineHeight: 1.3 }}>
                                {idx + 1}. {sub.titulo}
                              </h4>
                              
                              {isRoteiroActive && (
                                <span className="pulse-active-badge" style={{
                                  background: 'rgba(124, 58, 237, 0.08)',
                                  color: '#7c3aed',
                                  border: '1px solid rgba(124, 58, 237, 0.2)',
                                  padding: '2px 6px',
                                  borderRadius: '6px',
                                  fontSize: '0.55rem',
                                  fontWeight: 800,
                                  whiteSpace: 'nowrap'
                                }}>
                                  ● ATIVO
                                </span>
                              )}
                              {isRoteiroQueued && (
                                <span style={{
                                  background: '#eff6ff',
                                  color: '#1d4ed8',
                                  border: '1px solid #bfdbfe',
                                  padding: '2px 6px',
                                  borderRadius: '6px',
                                  fontSize: '0.55rem',
                                  fontWeight: 800,
                                  whiteSpace: 'nowrap'
                                }}>
                                  ⏳ FILA [{queuePosition}]
                                </span>
                              )}
                              {isRoteiroCompleted && (
                                <span style={{
                                  background: '#f0fdf4',
                                  color: '#15803d',
                                  border: '1px solid #bbf7d0',
                                  padding: '2px 6px',
                                  borderRadius: '6px',
                                  fontSize: '0.55rem',
                                  fontWeight: 800,
                                  whiteSpace: 'nowrap'
                                }}>
                                  ✅ OK
                                </span>
                              )}
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4 }}>
                              {sub.descricao || 'Sem descrição cadastrada.'}
                            </p>
                          </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 'auto', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                            ⏱️ {sub.duracao || '2m 30s'}
                          </span>
                          <span
                            style={{
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              color: sub.dificuldade === 'Avançado' ? '#ef4444' : sub.dificuldade === 'Intermediário' ? '#d97706' : '#10b981',
                              background: sub.dificuldade === 'Avançado' ? 'rgba(239,68,68,0.08)' : sub.dificuldade === 'Intermediário' ? 'rgba(217,119,6,0.08)' : 'rgba(16,185,129,0.08)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                            }}
                          >
                            {sub.dificuldade || 'Básico'}
                          </span>
                          {sub.pdfUrl && (
                            <a
                              href={sub.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                color: '#ef4444',
                                background: 'rgba(239, 68, 68, 0.08)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)';
                              }}
                            >
                              📄 Manual PDF
                            </a>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ width: '100%', background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.05) 0%, rgba(217, 70, 239, 0.01) 100%)', border: '1px solid rgba(124, 58, 237, 0.15)', borderRadius: '12px', padding: '20px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <Bot size={24} style={{ color: '#7c3aed', flexShrink: 0 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <h4 style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Alice Copiloto AI Studio
                  </h4>
                  <p style={{ fontSize: '0.78rem', color: '#475569', margin: 0, lineHeight: 1.5 }}>
                    Este tópico de capacitação está selecionado no contexto da IA. Você pode pedir para a Alice criar ou sugerir novos roteiros, preencher suas descrições ou planejar os passos de automação adequados conversando com ela no chat.
                  </p>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic', marginTop: '4px' }}>
                    Dica: "Sugerir novos roteiros para o tópico '{selectedTopico?.titulo}'."
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Resultados da Busca do Tópico
            (() => {
              const query = searchQuery.trim().toLowerCase();
              const matchedTopicSubtopics = selectedTopico?.subtopicos.filter((s) =>
                s.titulo.toLowerCase().includes(query) || (s.descricao && s.descricao.toLowerCase().includes(query))
              ) || [];
              const hasTopicMatches = matchedTopicSubtopics.length > 0;

              return (
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '20px' }}>
                  {hasTopicMatches && (
                    <div>
                      <h4 style={{ fontSize: '0.82rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>
                        Roteiros Encontrados ({matchedTopicSubtopics.length})
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                        {matchedTopicSubtopics.map((sub) => (
                          <div
                            key={sub.id}
                            onClick={() => setSelectedSubtopicoId(sub.id)}
                            style={{
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '10px',
                              padding: '16px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#7c3aed';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.02)';
                            }}
                          >
                            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                              📖 {sub.titulo}
                            </span>
                            <span style={{ fontSize: '0.72rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {sub.descricao || 'Sem descrição'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!hasTopicMatches && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '0.85rem' }}>
                      Nenhum resultado encontrado para "{searchQuery}".
                    </div>
                  )}
                </div>
              );
            })()
          )}
        </div>

        {/* Coluna da Direita: Painel de Processamento do Tópico */}
        <EcadProcessSidebar
          title="Processamento Tópico"
          subtitle={`Monitore e execute automações de ${selectedTopico?.titulo}`}
          type="topico"
          name={selectedTopico?.titulo}
          totalRoteiros={selectedTopico?.subtopicos.length || 0}
          pendingCount={metrics.pendentes}
          onTriggerAction={onTriggerAction}
          status={globalProcStatus}
          setStatus={setGlobalProcStatus}
          progress={globalProcProgress}
          setProgress={setGlobalProcProgress}
          timer={globalProcTimer}
          setTimer={setGlobalProcTimer}
          logs={globalProcLogs}
          setLogs={setGlobalProcLogs}
          autoShutdown={globalAutoShutdown}
          setAutoShutdown={setGlobalAutoShutdown}
          handleStartProcess={handleStartProcess}
          handleStopContainers={handleStopContainers}
          handleReset={handleReset}
        />

      </div>
    </div>
  );
}
